import { motion } from "framer-motion";
import { MapPin, ArrowUpRight, Compass, BookOpen, HandCoins, Layers } from "lucide-react";
import { MaskReveal, FadeUp } from "./Reveal";
import { HeroVisual } from "./HeroVisual";
import {
  HERO,
  GAP,
  APPROACH,
  OFFERINGS,
  OUTCOMES,
  CLIENTS,
  WORK,
  ABOUT,
  CONTACT,
  MEDIA,
} from "../data/content";

const Overline = ({ children, testid, light = false }) => (
  <p
    data-testid={testid}
    className={`text-xs md:text-sm uppercase tracking-[0.28em] font-semibold ${
      light ? "text-paper/70" : "text-clay"
    }`}
  >
    {children}
  </p>
);

// Section label marker (eyebrow) with a subtle divider. No numbering.
const SectionIndex = ({ label, light = false, center = false }) => (
  <div
    className={`flex items-center gap-3 text-xs uppercase tracking-[0.28em] font-semibold ${
      center ? "justify-center" : ""
    } ${light ? "text-paper/70" : "text-ink-soft"}`}
  >
    <span className={`h-px w-10 ${light ? "bg-paper/40" : "bg-ink/30"}`} />
    <span>{label}</span>
  </div>
);

const HEADING = "font-serif font-medium text-ink tracking-tight text-3xl md:text-4xl lg:text-5xl leading-[1.08]";
// Single consistent treatment for all subsection headings.
const SUBHEADING = "font-serif font-semibold text-ink tracking-tight text-2xl md:text-[1.75rem] leading-snug";

/* ---------------- HERO ---------------- */
export const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="px-6 md:px-10 pt-40 pb-24 md:pt-48 md:pb-32 min-h-[86vh] flex items-center"
  >
    <div className="mx-auto max-w-6xl w-full grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-7">
        <MaskReveal
          as="h1"
          onLoad
          delay={0.15}
          lines={HERO.headlineLines}
          className="font-serif font-medium text-ink text-3xl md:text-5xl lg:text-[3.5rem] leading-[1.12] tracking-tight max-w-3xl"
        />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 1 }}
          className="mt-10 max-w-2xl text-lg md:text-xl leading-loose text-ink-soft"
        >
          {HERO.subhead}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-12"
        >
          <a
            href="#contact"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-3 bg-ink text-paper px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-clay transition-colors duration-500"
          >
            {HERO.cta}
            <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 1 }}
        className="hidden lg:flex lg:col-span-5 justify-center"
      >
        <HeroVisual />
      </motion.div>
    </div>
  </section>
);

/* ---------------- THE GAP ---------------- */
export const Gap = () => (
  <section id="gap" data-testid="gap-section" className="py-24 md:py-36 bg-surface border-y border-line">
    <div className="mx-auto max-w-5xl px-6 md:px-10">
      <SectionIndex n="01" label="The Gap" />
      <MaskReveal
        as="h2"
        lines={GAP.headingLines}
        className={`mt-8 ${HEADING} max-w-3xl`}
      />
      <div className="mt-16 grid md:grid-cols-2 gap-px bg-line border border-line">
        {GAP.problems.map((p, i) => (
          <FadeUp key={i} className="h-full" delay={i * 0.05}>
            <div
              data-testid={`gap-problem-${i + 1}`}
              className="h-full bg-paper p-9 md:p-12 flex items-start"
            >
              <p className="text-base md:text-[1.0625rem] leading-[1.85] text-ink max-w-md">
                {p}
              </p>
            </div>
          </FadeUp>
        ))}
        {/* Solution line — concluding footer of the same unit */}
        <FadeUp className="md:col-span-2" delay={0.2}>
          <div data-testid="gap-solution" className="bg-ink px-8 md:px-12 py-8 md:py-10">
            <p className="text-base md:text-lg leading-[1.7] text-paper text-center">
              {GAP.solution}
            </p>
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

/* ---------------- APPROACH ---------------- */
export const Approach = () => (
  <section id="approach" data-testid="approach-section">
    {/* Integrated horizontal banner — text + illustration in one continuous block */}
    <div className="bg-surface border-y border-line">
      <div className="grid md:grid-cols-2 md:items-stretch">
        <div className="px-6 md:px-10 py-16 md:py-24 flex items-center md:justify-end">
          <div className="max-w-md">
            <SectionIndex n="02" label="Approach" />
            <MaskReveal as="h2" lines={APPROACH.headingLines} className={`mt-8 ${HEADING}`} />
            <FadeUp>
              <p className="mt-8 text-lg md:text-xl leading-[1.8] text-ink-soft">
                {APPROACH.intro}
              </p>
            </FadeUp>
          </div>
        </div>
        <FadeUp className="min-h-[280px] md:min-h-[460px] flex items-center md:justify-start justify-center px-6 py-6 md:py-10">
          <img
            data-testid="approach-visual"
            src="/approach-visual.webp"
            alt="Illustration of a strategist thinking through communication with ideas and data around her"
            loading="lazy"
            className="w-full max-w-[380px] h-full max-h-[400px] object-contain"
          />
        </FadeUp>
      </div>
    </div>
    <div className="px-6 md:px-10 py-20 md:py-28">
      <div className="mx-auto max-w-6xl grid md:grid-cols-2 gap-6 md:gap-8">
        {APPROACH.blocks.map((b, i) => (
          <FadeUp key={i} delay={i * 0.08}>
            <div
              data-testid={`approach-block-${i + 1}`}
              className="h-full bg-surface p-9 md:p-11"
            >
              <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-ink-soft">
                {`0${i + 1}`}
              </p>
              <h3 className="mt-5 font-serif font-semibold text-ink text-2xl md:text-[1.75rem] leading-tight tracking-tight">
                {b.title}
              </h3>
              <p className="mt-5 text-base md:text-[1.0625rem] leading-[1.85] text-ink-soft max-w-md">
                {b.text}
              </p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- IMAGE BAND (visual relief) ---------------- */
export const ImageBand = () => (
  <section data-testid="image-band" className="relative overflow-hidden bg-surface">
    <img
      src={MEDIA.band}
      alt="Two women in conversation across a table — Let's make your story easier to tell"
      loading="lazy"
      className="w-full h-auto md:h-[64vh] md:object-cover object-center"
    />
  </section>
);

/* ---------------- OFFERINGS ---------------- */
export const Offerings = () => (
  <section id="offerings" data-testid="offerings-section" className="bg-surface border-y border-line">
    {/* Integrated horizontal banner — text + illustration in one continuous block */}
    <div className="grid md:grid-cols-2 md:items-stretch">
      <div className="px-6 md:px-10 py-20 md:py-28 flex items-center md:justify-end">
        <div className="max-w-md">
          <SectionIndex label="Offerings" />
          <MaskReveal as="h2" lines={OFFERINGS.headingLines} className={`mt-8 ${HEADING}`} />
          <FadeUp>
            <p className="mt-8 text-lg md:text-xl leading-loose text-ink-soft">
              {OFFERINGS.intro}
            </p>
          </FadeUp>
        </div>
      </div>
      <FadeUp className="min-h-[280px] md:min-h-[460px] flex items-center md:justify-start justify-center px-6 py-6 md:py-10">
        <img
          data-testid="offerings-visual"
          src="/ways-to-work.webp"
          alt="Line drawing of a fist bump over a desk with reports, a laptop, planner and phone"
          loading="lazy"
          className="w-full max-w-[420px] h-full max-h-[400px] object-contain"
        />
      </FadeUp>
    </div>

    <div className="px-6 md:px-10 pb-24 md:pb-32">
      <div className="mx-auto max-w-6xl">
        {OFFERINGS.groups.map((g, gIdx) => (
          <div key={gIdx} className={gIdx > 0 ? "mt-16 md:mt-20" : ""}>
            <FadeUp>
              <h3 className="font-serif font-semibold text-ink tracking-tight text-3xl md:text-4xl leading-tight">
                {g.category}
              </h3>
              <p className="mt-4 text-lg md:text-xl leading-loose text-ink-soft max-w-2xl">
                {g.description}
              </p>
            </FadeUp>
            <div className="mt-10 md:mt-12 grid md:grid-cols-2 gap-6 md:gap-8 items-stretch">
              {g.cards.map((c, cIdx) => (
                <FadeUp key={cIdx} delay={cIdx * 0.08} className="h-full">
                  <div
                    data-testid={`offering-card-${gIdx}-${cIdx}`}
                    className="h-full flex flex-col bg-paper p-9 md:p-11"
                  >
                    <h4 className="font-serif font-semibold text-ink tracking-tight text-2xl md:text-[1.75rem] leading-tight md:min-h-[2.5em]">
                      {c.title}
                    </h4>
                    <p className="mt-5 text-base md:text-[1.0625rem] leading-[1.85] text-ink">
                      {c.description}
                    </p>
                    <div className="mt-8 pt-8 border-t border-line">
                      <ul className="space-y-3">
                        {c.list.map((item, lIdx) => (
                          <li key={lIdx} className="text-sm md:text-[15px] leading-[1.7] text-ink-soft flex items-start gap-3">
                            <span className="mt-[0.65em] h-px w-3 bg-clay shrink-0" aria-hidden="true" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- OUTCOMES ---------------- */
const OUTCOME_ICONS = [BookOpen, HandCoins, Layers, Compass];
export const Outcomes = () => (
  <section
    id="outcomes"
    data-testid="outcomes-section"
    className="px-6 md:px-10 py-24 md:py-36 bg-surface border-y border-line"
  >
    <div className="mx-auto max-w-6xl">
      <div className="max-w-5xl">
        <SectionIndex label="Outcomes" />
        <MaskReveal
          as="h2"
          lines={[
            "Better communication should change how the",
            "organisation works—not just how the words sound.",
          ]}
          className="mt-8 font-serif font-medium text-ink text-3xl md:text-[2.25rem] lg:text-[2.5rem] leading-[1.15] tracking-tight"
        />
        <FadeUp>
          <p className="mt-8 max-w-3xl text-lg md:text-xl leading-[1.8] text-ink-soft">
            {OUTCOMES.intro}
          </p>
        </FadeUp>
      </div>

      <div className="mt-20 md:mt-24 grid md:grid-cols-2 gap-x-16 gap-y-16 md:gap-y-20">
        {OUTCOMES.items.map((o, i) => {
          const Icon = OUTCOME_ICONS[i];
          return (
            <FadeUp key={i} delay={i * 0.08}>
              <div data-testid={`outcome-${i + 1}`} className="max-w-md">
                <div className="flex items-center gap-4">
                  <Icon size={26} strokeWidth={1.25} className="text-clay shrink-0" />
                  <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-clay">
                    {`0${i + 1}`}
                  </p>
                </div>
                <h3 className="mt-6 font-serif font-semibold text-ink text-2xl md:text-[1.75rem] leading-tight tracking-tight">
                  {o.title}
                </h3>
                <p className="mt-5 text-base md:text-[1.0625rem] leading-[1.85] text-ink-soft">
                  {o.text}
                </p>
              </div>
            </FadeUp>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- SELECTED WORK (text-led, no images) ---------------- */
/* ---------------- SELECTED WORK (text-led) ---------------- */
export const Work = () => (
  <section id="work" data-testid="work-section" className="px-6 md:px-10 py-24 md:py-36">
    <div className="mx-auto max-w-6xl">
      <SectionIndex label="Selected Work" />
      <FadeUp>
        <p className="mt-8 max-w-2xl text-lg md:text-xl leading-loose text-ink-soft">
          {WORK.intro}
        </p>
      </FadeUp>
      <div className="mt-16 grid md:grid-cols-2 gap-8 items-stretch">
        {WORK.cases.map((w, i) => (
          <FadeUp key={w.name} delay={i * 0.08}>
            <article
              data-testid={`work-case-${i}`}
              className="h-full flex flex-col bg-paper border border-line p-9 md:p-12"
            >
              <div className="h-[3px] w-14 bg-surface" aria-hidden="true" />
              <p className="mt-6 text-[11px] uppercase tracking-[0.24em] font-semibold text-ink-soft md:min-h-[2.6em]">
                {w.type}
              </p>
              <h3 className="mt-3 font-serif font-semibold text-ink tracking-tight text-3xl md:text-4xl leading-[1.1]">
                <a
                  href={w.url}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-clay transition-colors"
                >
                  {w.name}
                </a>
              </h3>
              <div className="mt-8 flex-1">
                <p className="text-[11px] uppercase tracking-[0.24em] font-semibold text-ink-soft">
                  The brief
                </p>
                <p className="mt-3 text-base md:text-[1.0625rem] leading-[1.85] text-ink-soft">
                  {w.brief}
                </p>
                <p className="mt-8 text-[11px] uppercase tracking-[0.24em] font-semibold text-ink-soft">
                  What I delivered
                </p>
                <p className="mt-3 text-base md:text-[1.0625rem] leading-[1.85] text-ink-soft">
                  {w.delivered}
                </p>
              </div>
            </article>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- CLIENTS ---------------- */
export const Clients = () => (
  <section id="clients" data-testid="clients-section" className="bg-surface border-y border-line">
    <div className="mx-auto max-w-6xl px-6 md:px-10 pt-14 md:pt-16">
      <SectionIndex label="Who I work best with" />
    </div>
    <FadeUp>
      <div className="mt-6 md:mt-8 mb-4">
        <img
          data-testid="clients-visual"
          src="/clients-banner.webp"
          alt="I work with nonprofits, foundations, CSR teams and other mission-led organisations tackling complex social and environmental challenges. This includes work across education, healthcare and mental health, livelihoods, inclusion, climate and sustainability, renewable energy, responsible consumption, philanthropy and CSR."
          loading="lazy"
          className="w-full h-auto block"
        />
      </div>
    </FadeUp>
  </section>
);

/* ---------------- ABOUT ---------------- */
export const About = () => (
  <section id="about" data-testid="about-section" className="px-6 md:px-10 py-24 md:py-36">
    <div className="mx-auto max-w-3xl">
      <SectionIndex label="About Aarti" />
      <MaskReveal
        as="h2"
        lines={["Senior judgment.", "Practical direction."]}
        className={`mt-8 ${HEADING}`}
      />

      {/* Small, subtle supporting photograph */}
      <FadeUp>
        <div className="mt-10 flex items-center gap-5">
          <img
            src={ABOUT.image}
            alt="Aarti Krishnan"
            loading="lazy"
            className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover shrink-0"
          />
          <div>
            <p className="font-serif text-xl font-semibold text-ink leading-none">Aarti Krishnan</p>
            <p className="mt-2 text-xs uppercase tracking-[0.24em] text-ink-soft">Founder, Gaatha</p>
          </div>
        </div>
      </FadeUp>

      <FadeUp>
        <blockquote className="mt-12 font-serif italic text-2xl md:text-3xl leading-snug text-ink font-normal">
          {ABOUT.quote}
        </blockquote>
      </FadeUp>
      <div className="mt-10 space-y-6">
        {ABOUT.paras.map((p, i) => (
          <FadeUp key={i}>
            <p className="text-base md:text-lg leading-loose text-ink-soft">{p}</p>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- CONTACT ---------------- */
export const Contact = () => (
  <section id="contact" data-testid="contact-section" className="px-6 md:px-10 py-28 md:py-44 bg-ink border-t border-line text-center">
    <div className="mx-auto max-w-2xl">
      <MaskReveal
        as="h2"
        lines={[CONTACT.heading]}
        className="font-serif font-medium text-paper text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight"
      />
      <FadeUp>
        <p className="mt-8 mx-auto max-w-xl text-base md:text-lg leading-[1.8] text-paper/85">
          {CONTACT.intro}
        </p>
      </FadeUp>

      <div className="mt-12">
        <a
          href={`mailto:${CONTACT.email}?subject=Start%20a%20conversation`}
          data-testid="contact-cta"
          className="inline-flex items-center bg-paper text-ink px-9 py-4 text-xs uppercase tracking-[0.2em] hover:bg-surface transition-colors duration-500"
        >
          Get in touch
        </a>
      </div>

      {/* Quiet supporting details */}
      <div className="mt-12 flex items-center justify-center gap-6 text-sm text-paper/70">
        <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="link-underline text-paper/80">
          {CONTACT.email}
        </a>
        <a href={CONTACT.linkedin} target="_blank" rel="noreferrer" data-testid="contact-linkedin-link" className="link-underline inline-flex items-center gap-1.5 text-paper/80">
          LinkedIn <ArrowUpRight size={14} />
        </a>
        <span className="inline-flex items-center gap-1.5">
          <MapPin size={14} className="text-paper/50" /> {CONTACT.location}
        </span>
      </div>
    </div>
  </section>
);

/* ---------------- FOOTER ---------------- */
export const Footer = () => (
  <footer data-testid="site-footer" className="px-6 md:px-10 py-14 bg-ink border-t border-paper/10">
    <div className="mx-auto max-w-7xl flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
      <p className="font-brand font-semibold text-paper tracking-[0.08em] text-3xl leading-none">Gaatha</p>
      <p className="text-sm text-paper/60 italic font-serif">
        Communication strategy for mission-led organisations
      </p>
      <p className="text-xs text-paper/50">
        &copy; {new Date().getFullYear()} Aarti Krishnan
      </p>
    </div>
  </footer>
);
